import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authenticatedGet, authenticatedPost } from '@/utils/api'
import { useRootStore } from './root'
import { useAuthStore } from './auth'
import { usePerimeterStore } from './perimeter'
import type { SCRComputationPayload } from './solvency'

interface SimulationComputationOptions {
  computeSCR?: boolean
  computeESG?: boolean
  computeRisk?: boolean
}

interface SimulationComputationResponse extends SCRComputationPayload {
  perimeter: {
    perimeterID: string
  }
}

export const useSimulationStore = defineStore('simulation', () => {
  const rootStore = useRootStore()
  const authStore = useAuthStore()
  const perimeterStore = usePerimeterStore()

  // State
  const simulationMode = ref(false)
  const transparised = ref(false)
  const simulationID = ref<string | undefined>(undefined)
  const perimeterId = ref<string | undefined>(undefined)
  const simulationGridReady = ref(false)
  const loading = ref(false)
  const quitSimulationPopupOpened = ref(false)
  const simulationHasChanges = ref(false) // Track if simulation data has been modified
  const resultsObsolete = ref(false) // Track if calculation results are obsolete
  const isRestoringAfterSimulation = ref(false) // Flag to prevent error toasts during restoration
  
  // Sauvegarde du périmètre avant simulation
  const savedPerimeter = ref<{
    client: any
    portfolios: any[]
    valuationDate: string | undefined
    compartments: any[]
    transparencyLevel: number | undefined
  } | null>(null)
  
  // Sauvegarde de l'état de transparisation avant simulation
  const savedTransparisation = ref<{
    loaded: boolean
    perimeterId: string | undefined
    transparisationDate: Date | undefined
    totalMarketValuation: number | null
  } | null>(null)

  // Sauvegarde de l'état solvency avant simulation
  const savedSolvency = ref<{
    scrDone: boolean
    scrMarketResult: any
    computationDate: string | undefined
  } | null>(null)

  // Computed
  const isSimulationMode = computed(() => simulationMode.value)
  const isTransparised = computed(() => transparised.value)

  // Actions
  const setSimulationMode = (value: boolean) => {
    simulationMode.value = value
  }

  const setTransparised = (value: boolean) => {
    transparised.value = value
  }

  const setSimulationGridReady = (value: boolean) => {
    simulationGridReady.value = value
  }

  const setQuitSimulationPopupOpened = (value: boolean) => {
    quitSimulationPopupOpened.value = value
  }

  const setSimulationHasChanges = (value: boolean) => {
    simulationHasChanges.value = value
    // Si on modifie la simulation, les résultats deviennent obsolètes
    if (value) {
      resultsObsolete.value = true
    }
  }

  const setResultsObsolete = (value: boolean) => {
    resultsObsolete.value = value
  }

  /**
   * Crée une simulation à partir du périmètre actuel
   */
  const createSimulationFromPerimeter = async () => {
    const account = authStore.userAccount
    if (!account) {
      throw new Error('User not authenticated')
    }

    // Sauvegarder le périmètre actuel avant de créer la simulation
    savedPerimeter.value = {
      client: perimeterStore.client,
      portfolios: [...perimeterStore.portfolios],
      valuationDate: perimeterStore.valuationDate,
      compartments: [...perimeterStore.compartments],
      transparencyLevel: perimeterStore.transparencyLevel,
    }

    // Sauvegarder aussi l'état de transparisation
    const transpaStore = await import('./transparisation').then(m => m.useTransparisationStore())
    savedTransparisation.value = {
      loaded: transpaStore.loaded,
      perimeterId: transpaStore.perimeterId,
      transparisationDate: transpaStore.transparisationDate,
      totalMarketValuation: transpaStore.totalMarketValuation,
    }

    // Sauvegarder l'état solvency avant simulation
    const { useSolvencyStore } = await import('./solvency')
    const solvencyStore = useSolvencyStore()
    savedSolvency.value = {
      scrDone: solvencyStore.scrDone,
      scrMarketResult: solvencyStore.scrMarketResult ? { ...solvencyStore.scrMarketResult } : undefined,
      computationDate: solvencyStore.computationDate,
    }

    try {
      const baseUrl = rootStore.baseUrl
      const userId = account.localAccountId || account.homeAccountId

      // Créer une simulation basée sur le périmètre actuel
      const response = await authenticatedPost(baseUrl + '/api/simulation/positions', {
        valuationDate: perimeterStore.valuationDate,
        rolename: perimeterStore.client?.code,
        portfolios: perimeterStore.portfolios.map(p => p.code),
        compartments: perimeterStore.compartments.map(c => c.code),
        lookThroughLevel: perimeterStore.transparencyLevel,
        userID: userId,
      })

      // L'API retourne directement le simulationID dans response.data
      simulationID.value = response.data
      simulationMode.value = true
      transparised.value = false
      simulationGridReady.value = false // Pas prêt tant qu'on n'a pas transparisé

      return response.data
    } catch (err) {
      console.error('Erreur création simulation:', err)
      throw err
    }
  }

  /**
   * Transparise l'inventaire simulé
   */
  const runSimulationComputation = async (options: SimulationComputationOptions = {}) => {
    const account = authStore.userAccount
    if (!account || !simulationID.value) {
      throw new Error('Simulation not initialized')
    }

    if (loading.value) {
      throw new Error('Un calcul est deja en cours')
    }

    loading.value = true

    try {
      const baseUrl = rootStore.baseUrl
      const userId = account.localAccountId || account.homeAccountId

      const response = await authenticatedPost<SimulationComputationResponse>(baseUrl + '/api/simulation/computation', {
        data: {
          userID: userId,
          simulationID: simulationID.value,
          computeEsg: options.computeESG || false,
          computeScr: options.computeSCR || false,
          computeRiskLiquidity: options.computeRisk || false,
        },
        timeoutProfile: 'compute',
      })

      perimeterId.value = response.data.perimeter.perimeterID
      transparised.value = true
      simulationGridReady.value = true // Maintenant la grid est prête
      simulationHasChanges.value = false // Réinitialiser après retransparisation
      resultsObsolete.value = false // Les résultats ne sont plus obsolètes après retransparisation

      if (options.computeSCR && response.data.globalSCRMarketResult) {
        const { useSolvencyStore } = await import('./solvency')
        const solvencyStore = useSolvencyStore()
        solvencyStore.setScrMarketResult(response.data)
        solvencyStore.setScrDone(true)
      }

      return response.data
    } catch (err) {
      console.error('Erreur transparisation simulation:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const computeSaveHierarchicalPositions = async (options?: SimulationComputationOptions) => {
    return runSimulationComputation(options)
  }

  /**
   * Lance un calcul sur la simulation (SCR, ESG, Risk)
   */
  const compute = async (options: SimulationComputationOptions) => {
    return runSimulationComputation(options)
  }

  const reset = () => {
    simulationMode.value = false
    transparised.value = false
    simulationID.value = undefined
    perimeterId.value = undefined
    simulationGridReady.value = false
    loading.value = false
    quitSimulationPopupOpened.value = false
    simulationHasChanges.value = false
    resultsObsolete.value = false
    savedPerimeter.value = null
    savedTransparisation.value = null
    savedSolvency.value = null
    // Note: isRestoringAfterSimulation n'est pas réinitialisé ici car il est géré par setTimeout
  }

  /**
   * Quitte le mode simulation et réinitialise tous les stores
   * Restaure le périmètre sauvegardé avant la simulation
   */
  const quitSimulation = () => {
    const perimeterSnapshot = savedPerimeter.value
      ? {
          client: savedPerimeter.value.client,
          portfolios: [...savedPerimeter.value.portfolios],
          valuationDate: savedPerimeter.value.valuationDate,
          compartments: [...savedPerimeter.value.compartments],
          transparencyLevel: savedPerimeter.value.transparencyLevel,
        }
      : null

    const transparisationSnapshot = savedTransparisation.value
      ? {
          loaded: savedTransparisation.value.loaded,
          perimeterId: savedTransparisation.value.perimeterId,
          transparisationDate: savedTransparisation.value.transparisationDate,
          totalMarketValuation: savedTransparisation.value.totalMarketValuation,
        }
      : null

    // Activer le flag de restauration pour éviter les toasts d'erreur
    isRestoringAfterSimulation.value = true

    // Restaurer le périmètre sauvegardé
    if (perimeterSnapshot) {
      perimeterStore.setClient(perimeterSnapshot.client)
      perimeterStore.setPortfolios(perimeterSnapshot.portfolios)
      perimeterStore.setValuationDate(perimeterSnapshot.valuationDate)
      perimeterStore.setCompartments(perimeterSnapshot.compartments)
      perimeterStore.setTransparencyLevel(perimeterSnapshot.transparencyLevel)
    }

    // Restaurer l'état de transparisation sauvegardé
    import('./transparisation').then(({ useTransparisationStore }) => {
      const transpaStore = useTransparisationStore()
      if (transparisationSnapshot) {
        transpaStore.loaded = transparisationSnapshot.loaded
        transpaStore.transparisationDate = transparisationSnapshot.transparisationDate
        transpaStore.totalMarketValuation = transparisationSnapshot.totalMarketValuation
        transpaStore.perimeterId = transparisationSnapshot.perimeterId
      }
    })

    // Réinitialiser le store simulation
    reset()

    // Désactiver le flag après un délai pour permettre au composant de se stabiliser
    setTimeout(() => {
      isRestoringAfterSimulation.value = false
    }, 1000)

    // Réinitialiser le store solvency (pas de restauration : l'utilisateur doit recalculer)
    import('./solvency').then(({ useSolvencyStore }) => {
      const solvencyStore = useSolvencyStore()
      if (solvencyStore.reset) solvencyStore.reset()
    })

    // DÉSACTIVÉ - Modules ESG et Risk
    // import('./esg').then(({ useEsgStore }) => {
    //   const esgStore = useEsgStore()
    //   if (esgStore.reset) esgStore.reset()
    // })

    // import('./risk').then(({ useRiskStore }) => {
    //   const riskStore = useRiskStore()
    //   if (riskStore.reset) riskStore.reset()
    // })
  }

  return {
    // State
    simulationMode,
    transparised,
    simulationID,
    perimeterId,
    simulationGridReady,
    loading,
    quitSimulationPopupOpened,
    simulationHasChanges,
    resultsObsolete,
    savedPerimeter,
    savedTransparisation,
    savedSolvency,
    isRestoringAfterSimulation,

    // Computed
    isSimulationMode,
    isTransparised,

    // Actions
    setSimulationMode,
    setTransparised,
    setSimulationGridReady,
    setQuitSimulationPopupOpened,
    setSimulationHasChanges,
    setResultsObsolete,
    createSimulationFromPerimeter,
    computeSaveHierarchicalPositions,
    compute,
    reset,
    quitSimulation,
  }
}, {
  persist: {
    key: 'arago-simulation',
    storage: sessionStorage, // sessionStorage pour sécurité
    pick: ['simulationMode', 'transparised', 'simulationID', 'perimeterId', 'simulationGridReady', 'resultsObsolete'],
  },
})
